import {
  assessReservationInventory,
  evaluateOverbookingDecision,
} from "../reservation-management/service.ts";
import type {
  ImportChannelReservationRequestDto,
  SyncChannelRatesAvailabilityRequestDto,
} from "./contracts/dtos.ts";
import {
  validateImportChannelReservationRequest,
  validateSyncChannelRatesAvailabilityRequest,
} from "./contracts/schemas.ts";
import type { ChannelProvider } from "./provider.ts";
import type {
  ChannelImportDecision,
  ChannelManagerContext,
  ChannelReservationRecord,
  ChannelSyncAttempt,
} from "./types.ts";

type IdFactory = (prefix: string) => string;

function hasErrors(fieldErrors: Record<string, string>) {
  return Object.values(fieldErrors).some(Boolean);
}

function createPayloadHash(input: Record<string, unknown>) {
  return JSON.stringify(input);
}

export function importChannelReservation(
  request: ImportChannelReservationRequestDto,
  context: ChannelManagerContext,
  dependencies: { idFactory: IdFactory }
) {
  const fieldErrors = validateImportChannelReservationRequest(request);
  if (hasErrors(fieldErrors)) {
    return {
      ok: false as const,
      code: "VALIDATION_ERROR" as const,
      message: "Channel reservation payload is invalid.",
      fieldErrors,
    };
  }

  const payloadHash = createPayloadHash(request.reservation);
  const existingLink = context.syncedReservations.find(
    (entry) =>
      entry.connectionId === request.connectionId &&
      entry.externalReservationId === request.reservation.externalReservationId
  );

  if (existingLink && existingLink.payloadHash === payloadHash) {
    return {
      ok: true as const,
      decision: {
        allowed: true,
        reason: "Duplicate reservation import skipped.",
        reservationAction: "skipped",
      } satisfies ChannelImportDecision,
      link: existingLink,
    };
  }

  const existingReservation = existingLink
    ? context.reservations.find((reservation) => reservation.id === existingLink.reservationId)
    : null;

  if (request.reservation.status === "cancelled") {
    if (!existingLink || !existingReservation) {
      return {
        ok: true as const,
        decision: {
          allowed: true,
          reason: "Cancellation skipped because reservation was not imported yet.",
          reservationAction: "skipped",
        } satisfies ChannelImportDecision,
        link: existingLink ?? null,
      };
    }

    const updatedLink: ChannelReservationRecord = {
      ...existingLink,
      externalStatus: "cancelled",
      payloadHash,
      lastSeenAt: context.now,
    };

    return {
      ok: true as const,
      decision: {
        allowed: true,
        reason: "External cancellation accepted.",
        reservationAction: "cancelled",
      } satisfies ChannelImportDecision,
      link: updatedLink,
      reservation: {
        ...existingReservation,
        status: "cancelled" as const,
        cancellationReason: `Cancelled by ${request.reservation.sourceLabel ?? "channel"}`,
        cancelledAt: context.now,
        cancelledBy: request.actor,
      },
    };
  }

  const inventorySnapshot = assessReservationInventory(
    request.reservation.roomTypeId,
    request.reservation.arrival,
    request.reservation.departure,
    request.reservation.rooms,
    {
      reservations: context.reservations.map((reservation) => ({
        id: reservation.id,
        guestId: reservation.guestId,
        arrival: reservation.arrival,
        departure: reservation.departure,
        roomTypeId: reservation.roomTypeId,
        rooms: reservation.rooms,
        status: reservation.status,
        createdAt: reservation.createdAt,
      })),
      waitlistEntries: [],
      settings: context.overbookingSettings,
      inventoryByRoomType: context.inventoryByRoomType,
      now: context.now,
    }
  );
  const overbookingDecision = evaluateOverbookingDecision(inventorySnapshot, false, {
    reservations: [],
    waitlistEntries: [],
    settings: context.overbookingSettings,
    inventoryByRoomType: context.inventoryByRoomType,
    now: context.now,
  });

  if (!overbookingDecision.allowed) {
    return {
      ok: false as const,
      code: "OVERBOOKING_BLOCKED" as const,
      message: overbookingDecision.reason,
    };
  }

  const reservationId = existingReservation?.id ?? request.reservation.externalReservationId.replace(/^EXT-/, "R-");
  const reservation = {
    id: reservationId,
    guestId: existingReservation?.guestId ?? dependencies.idFactory("guest"),
    arrival: request.reservation.arrival,
    departure: request.reservation.departure,
    adults: request.reservation.adults,
    children: request.reservation.children,
    rooms: request.reservation.rooms,
    ratePlanId: request.reservation.ratePlanId,
    roomTypeId: request.reservation.roomTypeId,
    status: "booked" as const,
    createdAt: existingReservation?.createdAt ?? context.now,
    source: request.reservation.sourceLabel ?? "OTA",
    reservationType: "single" as const,
    billingMode: "individual" as const,
  };

  const link: ChannelReservationRecord = {
    id: existingLink?.id ?? dependencies.idFactory("ch-res"),
    connectionId: request.connectionId,
    externalReservationId: request.reservation.externalReservationId,
    reservationId,
    externalStatus: request.reservation.status,
    payloadHash,
    importedAt: existingLink?.importedAt ?? context.now,
    lastSeenAt: context.now,
    sourceLabel: request.reservation.sourceLabel,
  };

  return {
    ok: true as const,
    decision: {
      allowed: true,
      reason: existingReservation ? "External reservation updated." : "External reservation imported.",
      reservationAction: existingReservation ? "updated" : "created",
    } satisfies ChannelImportDecision,
    reservation,
    link,
  };
}

export async function syncChannelRatesAndAvailability(
  request: SyncChannelRatesAvailabilityRequestDto,
  context: ChannelManagerContext & { attempts: ChannelSyncAttempt[] },
  dependencies: { idFactory: IdFactory; provider: ChannelProvider }
) {
  const fieldErrors = validateSyncChannelRatesAvailabilityRequest(request);
  if (hasErrors(fieldErrors)) {
    return {
      ok: false as const,
      code: "VALIDATION_ERROR" as const,
      message: "Sync request is invalid.",
      fieldErrors,
    };
  }

  const existingAttempt = context.attempts.find(
    (entry) => entry.connectionId === request.connectionId && entry.idempotencyKey === request.idempotencyKey
  );
  if (existingAttempt && existingAttempt.status === "success") {
    return {
      ok: true as const,
      attempt: {
        ...existingAttempt,
        status: "skipped" as const,
      },
      remoteReference: existingAttempt.id,
    };
  }

  const payloadHash = createPayloadHash({
    rates: request.rates,
    availability: request.availability,
    scope: request.scope,
  });
  const baseAttempt: ChannelSyncAttempt = {
    id: existingAttempt?.id ?? dependencies.idFactory("sync"),
    connectionId: request.connectionId,
    scope: request.scope,
    idempotencyKey: request.idempotencyKey,
    status: existingAttempt ? "retrying" : "pending",
    requestedAt: existingAttempt?.requestedAt ?? context.now,
    retryCount: existingAttempt ? existingAttempt.retryCount + 1 : 0,
    payloadHash,
  };

  try {
    const response = await dependencies.provider.pushRatesAndAvailability({
      connectionId: request.connectionId,
      rates: request.rates,
      availability: request.availability,
      idempotencyKey: request.idempotencyKey,
    });

    return {
      ok: true as const,
      attempt: {
        ...baseAttempt,
        status: response.status === "pending" ? "pending" as const : "success" as const,
        completedAt: response.status === "success" ? context.now : undefined,
      },
      remoteReference: response.remoteReference,
      delayed: response.delayed,
    };
  } catch (error) {
    return {
      ok: false as const,
      code: "SYNC_FAILED" as const,
      message: error instanceof Error ? error.message : "Channel synchronization failed.",
      attempt: {
        ...baseAttempt,
        status: "failed" as const,
        completedAt: context.now,
        errorMessage: error instanceof Error ? error.message : "Channel synchronization failed.",
      },
    };
  }
}
